import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Loader2, ShieldCheck, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";
import { Btn, Input, PasswordInput, Field } from "@/components/kit";

/**
 * Login — Midnight & Turmeric refresh with inline 2FA step.
 * Layout: left navy hero panel (gradient + subtle grain), right paper form panel.
 * Flow: username+password → if server responds `totp_required`, swap form to 6-digit
 * code + "Trust this device 30 days" without leaving the page.
 */
function marathiDate(d = new Date()) {
  const DAYS = ["रविवार", "सोमवार", "मंगळवार", "बुधवार", "गुरुवार", "शुक्रवार", "शनिवार"];
  const MONTHS = ["जानेवारी", "फेब्रुवारी", "मार्च", "एप्रिल", "मे", "जून", "जुलै", "ऑगस्ट", "सप्टेंबर", "ऑक्टोबर", "नोव्हेंबर", "डिसेंबर"];
  const digits = ["०", "१", "२", "३", "४", "५", "६", "७", "८", "९"];
  const toMar = (n) => String(n).split("").map((c) => digits[c] ?? c).join("");
  return `${DAYS[d.getDay()]} · ${toMar(d.getDate())} ${MONTHS[d.getMonth()]} ${toMar(d.getFullYear())}`;
}

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState("cred"); // cred | totp
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [trustDevice, setTrustDevice] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const payload = step === "totp"
        ? { username, password, totp_code: totpCode.trim(), trust_device: trustDevice }
        : { username, password };
      await login(payload);
      navigate("/");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // Server signals "password OK, now show 2FA form"
      if (detail && typeof detail === "object" && detail.code === "totp_required") {
        setStep("totp");
        setError("");
      } else if (detail && typeof detail === "object" && detail.code === "totp_invalid") {
        setError(detail.message || "Invalid 2FA code");
      } else {
        setError(apiErr(err));
      }
    }
    setBusy(false);
  };

  const backToCred = () => {
    setStep("cred"); setTotpCode(""); setError("");
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[color:var(--paper)]">
      {/* Left: navy hero panel */}
      <div className="hidden lg:flex relative flex-col justify-between p-12 overflow-hidden text-white"
        style={{ background: "linear-gradient(135deg, var(--navy) 0%, var(--navy-deep) 100%)" }}>
        {/* Turmeric radial glow */}
        <div className="absolute -top-20 -left-20 w-96 h-96 rounded-full opacity-25 blur-3xl"
          style={{ background: "radial-gradient(circle, var(--turmeric) 0%, transparent 60%)" }} />
        {/* Subtle grain */}
        <div className="absolute inset-0 opacity-[.06] mix-blend-overlay pointer-events-none"
          style={{ backgroundImage: 'url("data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22200%22 height=%22200%22><filter id=%22n%22><feTurbulence type=%22fractalNoise%22 baseFrequency=%22.9%22 numOctaves=%223%22 stitchTiles=%22stitch%22/></filter><rect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23n)%22/></svg>")' }} />

        <div className="relative flex items-center gap-3">
          <img src="/logo.webp" alt="SSVP" className="h-12 w-12 rounded-full object-cover bg-white ring-2"
            style={{ ringColor: "var(--turmeric)" }} />
          <div className="leading-tight">
            <div className="font-heading font-bold text-xl text-white">SSVP Account</div>
            <div className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: "var(--turmeric)" }}>Pro · Cloud</div>
          </div>
        </div>

        <div className="relative">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold mb-6"
            style={{ background: "var(--turmeric-soft)", color: "var(--turmeric)" }}>
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: "var(--turmeric)" }} />
            Multi-store accounting · Cloud
          </div>
          <h2 className="font-heading text-[44px] leading-[1.05] font-extrabold text-white tracking-tight">
            Multi-store accounting,<br />
            <span style={{ color: "var(--turmeric)" }}>one source of truth.</span>
          </h2>
          <p className="mt-5 max-w-md text-[15px] leading-relaxed" style={{ color: "#C7D2E4" }}>
            Daily reconciliation, material ledgers, payroll, and real-time P&L for every
            Shree Siddhivinayak Vada Pav outlet — synced to the cloud, secured with 2FA.
          </p>
          {/* Feature ticks */}
          <div className="mt-8 grid grid-cols-2 gap-x-6 gap-y-2 text-xs" style={{ color: "#C7D2E4" }}>
            {["Nightly backups", "Role-based access", "P&L in one tap", "Marathi + English"].map((f) => (
              <div key={f} className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full" style={{ background: "var(--chutney)" }} />
                {f}
              </div>
            ))}
          </div>
        </div>

        <div className="relative flex items-center justify-between text-[11px] uppercase tracking-widest" style={{ color: "#8FA1BC" }}>
          <span>{marathiDate()}</span>
          <span>INR · व्यापारी आवृत्ती</span>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img src="/logo.webp" alt="SSVP" className="h-11 w-11 rounded-full object-cover ring-1"
              style={{ ringColor: "var(--turmeric)" }} />
            <div>
              <div className="font-heading font-bold text-lg text-[color:var(--ink)]">SSVP Account</div>
              <div className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: "var(--turmeric)" }}>Pro · Cloud</div>
            </div>
          </div>

          {step === "cred" ? (
            <>
              <h1 className="font-heading text-3xl font-extrabold tracking-tight text-[color:var(--ink)]">Welcome back</h1>
              <p className="text-sm mt-1 mb-8" style={{ color: "var(--ink-soft)" }}>पुन्हा स्वागत आहे · Sign in to continue.</p>
            </>
          ) : (
            <>
              <button type="button" onClick={backToCred} data-testid="login-back-to-cred"
                className="flex items-center gap-1.5 text-xs font-semibold mb-4 hover:text-[color:var(--turmeric)]"
                style={{ color: "var(--ink-soft)" }}>
                <ArrowLeft className="h-3.5 w-3.5" /> Back
              </button>
              <div className="inline-flex items-center justify-center h-11 w-11 rounded-full mb-4"
                style={{ background: "var(--turmeric-soft)", color: "var(--turmeric)" }}>
                <ShieldCheck className="h-5 w-5" />
              </div>
              <h1 className="font-heading text-2xl font-extrabold tracking-tight text-[color:var(--ink)]">Two-factor code</h1>
              <p className="text-sm mt-1 mb-8" style={{ color: "var(--ink-soft)" }}>
                Open your Authenticator app and enter the 6-digit code for <span className="font-semibold text-[color:var(--ink)]">{username}</span>.
                <br />Or use a backup code (XXXX-XXXX).
              </p>
            </>
          )}

          <form onSubmit={submit} className="space-y-4">
            {step === "cred" ? (
              <>
                <Field label="Username">
                  <Input data-testid="login-username" value={username} onChange={(e) => setUsername(e.target.value)}
                    placeholder="e.g. admin" autoFocus autoComplete="username" />
                </Field>
                <Field label="Password">
                  <PasswordInput data-testid="login-password" value={password} onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••" autoComplete="current-password" />
                </Field>
              </>
            ) : (
              <>
                <Field label="Authenticator code">
                  <Input data-testid="login-totp" value={totpCode} onChange={(e) => setTotpCode(e.target.value.replace(/\s/g, ""))}
                    placeholder="123456 or XXXX-XXXX" inputMode="text" maxLength={12} autoFocus
                    className="font-mono-num tracking-widest text-center text-lg" />
                </Field>
                <label className="flex items-center gap-2 cursor-pointer select-none py-1"
                  style={{ color: "var(--ink-soft)" }}>
                  <input type="checkbox" data-testid="login-trust-device" checked={trustDevice}
                    onChange={(e) => setTrustDevice(e.target.checked)}
                    className="h-4 w-4 rounded accent-orange-700" />
                  <span className="text-sm">Trust this device for 30 days</span>
                </label>
              </>
            )}

            {error && (
              <p data-testid="login-error" className="text-sm rounded-md px-3 py-2 border-l-2"
                style={{ background: "var(--tamarind-soft)", color: "var(--tamarind)", borderColor: "var(--tamarind)" }}>
                {error}
              </p>
            )}

            <Btn data-testid="login-submit" type="submit" disabled={busy} className="w-full">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              {step === "cred" ? "Sign In" : "Verify & Continue"}
            </Btn>
          </form>

          <p className="text-xs mt-8 text-center" style={{ color: "var(--ink-soft)" }}>
            Locked out? Ask an admin to reset your 2FA.
          </p>
        </div>
      </div>
    </div>
  );
}
